import type {
  Issue,
  Config as LinterConfig,
  LinterResult
} from '@digibee/linter';
import { wrap } from 'comlink';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { generateFlowSpecFromStores } from './helpers';

import {
  useConnectorStore,
  useDesignUIStore,
  useTrackStore,
  useTriggerStore
} from '.';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import { getFeatureFlag } from '~/common/helpers/globalFeatureFlags';
import type { Nullish } from '~/common/types/Nullish';
import type { LinterWorker } from '~/scenes/Build/scenes/FlowBuilder/components/Design/worker';

export type LinterConfigWithDisabled = LinterConfig & { disabled: boolean };

export type LinterResultWithIssues = LinterResult & {
  issues: [string, Issue[]][];
  count: number;
};

type LinterState = {
  linterConfig: LinterConfigWithDisabled;
  linterResult: LinterResultWithIssues;
  showLinterOnCanvas: boolean;
  showingLinterTooltip: string | undefined;
};

type LinterActions = {
  setLinterConfig: (config: LinterConfigWithDisabled) => void;
  setLinterResult: (result: LinterResultWithIssues) => void;
  toggleLinterOnCanvas: () => void;
  enableLinter: () => void;
  disableLinter: () => void;
  setShowingLinterTooltip: (elementId: string | undefined) => void;
  clearShowingLinterTooltip: () => void;
  setIssues: (params: {
    linterResult: LinterResult;
    tracksNames: string[];
    isCapsule: boolean;
  }) => void;
  reset: () => void;
};

type LinterStore = LinterState & LinterActions;

const initialLinterResult: LinterResultWithIssues = {
  hasError: false,
  isInvalid: false,
  trigger: [],
  tracks: {},
  components: {},
  issues: [],
  count: 0
};

const initialState: LinterState = {
  linterConfig: {
    disabled: false,
    rules: {}
  },
  linterResult: initialLinterResult,
  showLinterOnCanvas: true,
  showingLinterTooltip: undefined
};

export const useLinterStore = create<LinterStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setLinterConfig: config => set({ linterConfig: config }),

      setLinterResult: result => set({ linterResult: result }),

      toggleLinterOnCanvas: () =>
        set(state => {
          state.showLinterOnCanvas = !state.showLinterOnCanvas;
        }),

      enableLinter: () =>
        set(state => {
          state.showLinterOnCanvas = true;
        }),

      disableLinter: () =>
        set(state => {
          state.showLinterOnCanvas = false;
        }),

      setShowingLinterTooltip: elementId =>
        set(state => {
          state.showingLinterTooltip = elementId;
        }),

      clearShowingLinterTooltip: () =>
        set(state => {
          state.showingLinterTooltip = undefined;
        }),

      setIssues: ({ linterResult, tracksNames, isCapsule }) =>
        set(state => {
          const componentsIssues = tracksNames.flatMap(trackName => {
            const trackComponentsIssues =
              linterResult.components?.[trackName] || {};

            return Object.entries(trackComponentsIssues).filter(
              ([, currentIssues]) => currentIssues.length > 0
            );
          });

          const triggerIssues =
            linterResult.trigger.length > 0 && !isCapsule
              ? ['trigger', linterResult.trigger]
              : [];

          const issues = [triggerIssues, ...componentsIssues].filter(
            item => item.length > 0
          ) as [string, Issue[]][];

          const count = issues.reduce(
            (acc, [, componentIssues]) => acc + (componentIssues?.length || 0),
            0
          );

          const trigger = isCapsule ? [] : linterResult.trigger;

          state.linterResult = {
            ...linterResult,
            trigger,
            issues,
            count
          };
        }),

      reset: () => set({ ...initialState })
    })),
    { name: 'LinterStore' }
  )
);

export const setLinterConfig = (config: LinterConfigWithDisabled) =>
  useLinterStore.getState().setLinterConfig(config);

export const setLinterResult = (result: LinterResultWithIssues) =>
  useLinterStore.getState().setLinterResult(result);

export const toggleLinterOnCanvas = () =>
  useLinterStore.getState().toggleLinterOnCanvas();

export const enableLinter = () => useLinterStore.getState().enableLinter();

export const disableLinter = () => useLinterStore.getState().disableLinter();

export const setShowingLinterTooltip = (elementId: string | undefined) =>
  useLinterStore.getState().setShowingLinterTooltip(elementId);

export const clearShowingLinterTooltip = () =>
  useLinterStore.getState().clearShowingLinterTooltip();

export const setIssues = (params: {
  linterResult: LinterResult;
  tracksNames: string[];
  isCapsule: boolean;
}) => useLinterStore.getState().setIssues(params);

export const resetLinterStore = () => useLinterStore.getState().reset();

export const isLinterEnabled = () =>
  !useLinterStore.getState().linterConfig.disabled;

export const getLinterIssuesCount = () =>
  useLinterStore.getState().linterResult.count;

export const getLinterIssues = () =>
  useLinterStore.getState().linterResult.issues;

export const useLinterIssues = () => {
  const linterResult = useLinterStore(state => state.linterResult);
  const showLinterOnCanvas = useLinterStore(state => state.showLinterOnCanvas);

  return {
    issues: linterResult?.issues,
    hasError: linterResult?.hasError,
    count: linterResult?.count,
    showLinterOnCanvas
  };
};

let lastLinterWorker: Nullish<Worker> = null;

export const runLinter = async () => {
  const { connectors } = useConnectorStore.getState();
  const triggerSpec = useTriggerStore.getState().trigger;

  const isDoubleBracesAliasLinterEnabled = getFeatureFlag(
    featureFlagConstants.BUILD_ENABLE_DOUBLE_BRACES_ALIAS_LINTER
  );

  if (lastLinterWorker) lastLinterWorker.terminate();

  const flowSpec = generateFlowSpecFromStores();

  const worker = new Worker(
    new URL('../components/Design/worker', import.meta.url),
    {
      name: 'linter-worker',
      type: 'module'
    }
  );

  lastLinterWorker = worker;

  const workerApi = wrap<LinterWorker>(worker);

  const linterResultPromise = workerApi.linter({
    flowSpec,
    triggerSpec,
    connectors,
    config: {
      rules: {
        'double-braces-aliases-validator': {
          disabled: !isDoubleBracesAliasLinterEnabled
        }
      }
    }
  });

  const { tracks } = useTrackStore.getState();
  const linterResult = await linterResultPromise;
  setIssues({
    linterResult,
    tracksNames: Array.from(tracks.values()).map(track => track.name),
    isCapsule: useDesignUIStore.getState().isCapsule
  });
};
